<script>
import { useVuelidate } from '@vuelidate/core';
import { required, url, helpers } from '@vuelidate/validators';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useConfig } from 'dashboard/composables/useConfig';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import NextInput from 'next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import BuildInfo from './components/BuildInfo.vue';
import AccountDelete from './components/AccountDelete.vue';
import AutoResolve from './components/AutoResolve.vue';
import AudioTranscription from './components/AudioTranscription.vue';
import IcNumberValidation from './components/IcNumberValidation.vue';
import SectionLayout from './components/SectionLayout.vue';
import { timeZoneOptions } from '../inbox/helpers/businessHour';
import { normalizeBrandLogoUrl } from 'dashboard/helper/accountBranding';

const MAXIMUM_LOGO_FILE_SIZE = 4;

export default {
  components: {
    NextButton,
    Avatar,
    BuildInfo,
    AccountDelete,
    AutoResolve,
    AudioTranscription,
    IcNumberValidation,
    SectionLayout,
    NextInput,
  },
  setup() {
    const { updateUISettings, uiSettings } = useUISettings();
    const { enabledLanguages } = useConfig();
    const { accountId } = useAccount();
    const v$ = useVuelidate();

    return { updateUISettings, uiSettings, v$, enabledLanguages, accountId };
  },
  data() {
    return {
      id: '',
      name: '',
      locale: 'en',
      timezone: 'Etc/UTC',
      domain: '',
      logo: '',
      logoBlobId: '',
      logoDirty: false,
      isUploadingLogo: false,
      supportEmail: '',
      features: {},
      chatButton1Name: '',
      chatButton1Url: '',
      chatButton2Name: '',
      chatButton2Url: '',
      chatButton3Name: '',
      chatButton3Url: '',
      timezoneOptions: timeZoneOptions(),
    };
  },
  validations: {
    name: {
      required,
    },
    locale: {
      required,
    },
    timezone: {
      required,
    },
    chatButton1Url: {
      url: helpers.withMessage(
        'invalid_url',
        val => !val || url.$validator(val)
      ),
    },
    chatButton2Url: {
      url: helpers.withMessage(
        'invalid_url',
        val => !val || url.$validator(val)
      ),
    },
    chatButton3Url: {
      url: helpers.withMessage(
        'invalid_url',
        val => !val || url.$validator(val)
      ),
    },
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      uiFlags: 'accounts/getUIFlags',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      isOnChatwootCloud: 'globalConfig/isOnChatwootCloud',
    }),
    showAutoResolutionConfig() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.AUTO_RESOLVE_CONVERSATIONS
      );
    },
    showAudioTranscriptionConfig() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.CAPTAIN
      );
    },
    languagesSortedByCode() {
      const enabledLanguages = [...this.enabledLanguages];
      return enabledLanguages.sort((l1, l2) =>
        l1.iso_639_1_code.localeCompare(l2.iso_639_1_code)
      );
    },
    isUpdating() {
      return this.uiFlags.isUpdating;
    },
    featureInboundEmailEnabled() {
      return !!this.features?.inbound_emails;
    },
    featureCustomReplyDomainEnabled() {
      return (
        this.featureInboundEmailEnabled && !!this.features.custom_reply_domain
      );
    },
    featureCustomReplyEmailEnabled() {
      return (
        this.featureInboundEmailEnabled && !!this.features.custom_reply_email
      );
    },
    currentAccount() {
      return this.getAccount(this.accountId) || {};
    },
    accountNameHelpText() {
      return this.$t('GENERAL_SETTINGS.FORM.ACCOUNT_NAME_HELP');
    },
    brandLogoPreview() {
      return normalizeBrandLogoUrl(this.logo);
    },
    brandLogoLabel() {
      return this.$t('GENERAL_SETTINGS.FORM.BRAND_LOGO.LABEL');
    },
    brandLogoHelpText() {
      return this.$t('GENERAL_SETTINGS.FORM.BRAND_LOGO.HELP');
    },
    brandLogoSizeHelpText() {
      return this.$t('GENERAL_SETTINGS.FORM.BRAND_LOGO.SIZE_HELP', { size: MAXIMUM_LOGO_FILE_SIZE });
    },
    appIdLabel() {
      return this.$t('GENERAL_SETTINGS.FORM.ACCOUNT_ID.TITLE');
    },
    logoUploadSuccessMessage() {
      return this.$t('GENERAL_SETTINGS.FORM.BRAND_LOGO.UPLOAD_SUCCESS');
    },
    logoUploadErrorMessage() {
      return this.$t('GENERAL_SETTINGS.FORM.BRAND_LOGO.UPLOAD_ERROR');
    },
    logoUploadSizeErrorMessage() {
      return this.$t('GENERAL_SETTINGS.FORM.BRAND_LOGO.SIZE_ERROR', { size: MAXIMUM_LOGO_FILE_SIZE });
    },
    logoDeleteHelpText() {
      return this.$t('GENERAL_SETTINGS.FORM.BRAND_LOGO.DELETE_HELP');
    },
    logoUploadingText() {
      return this.$t('GENERAL_SETTINGS.FORM.BRAND_LOGO.UPLOADING');
    },
  },
  mounted() {
    this.initializeAccount();
  },
  methods: {
    normalizeTimezone(timezone) {
      if (!timezone || timezone === 'UTC') {
        return 'Etc/UTC';
      }
      return timezone;
    },
    async initializeAccount() {
      try {
        const {
          name,
          locale,
          id,
          domain,
          logo,
          support_email,
          timezone,
          features,
          settings,
          custom_attributes,
        } = this.getAccount(this.accountId);

        this.$root.$i18n.locale = this.uiSettings?.locale || locale;
        this.name = name;
        this.locale = locale;
        this.timezone = this.normalizeTimezone(timezone);
        this.id = id;
        this.domain = domain;
        this.logo = (logo || custom_attributes?.logo || '').toString().trim();
        this.logoBlobId = '';
        this.logoDirty = false;
        this.isUploadingLogo = false;
        this.supportEmail = support_email;
        this.features = features;
        this.chatButton1Name = settings?.chat_button_1_name || '';
        this.chatButton1Url = settings?.chat_button_1_url || '';
        this.chatButton2Name = settings?.chat_button_2_name || '';
        this.chatButton2Url = settings?.chat_button_2_url || '';
        this.chatButton3Name = settings?.chat_button_3_name || '';
        this.chatButton3Url = settings?.chat_button_3_url || '';
      } catch (error) {
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.ERROR'));
      }
    },

    async updateAccount() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        useAlert(this.$t('GENERAL_SETTINGS.FORM.ERROR'));
        return;
      }
      try {
        const updatePayload = {
          locale: this.locale,
          name: this.name,
          timezone: this.normalizeTimezone(this.timezone),
          domain: this.domain,
          support_email: this.supportEmail,
          chat_button_1_name: this.chatButton1Name,
          chat_button_1_url: this.chatButton1Url,
          chat_button_2_name: this.chatButton2Name,
          chat_button_2_url: this.chatButton2Url,
          chat_button_3_name: this.chatButton3Name,
          chat_button_3_url: this.chatButton3Url,
        };

        if (this.logoBlobId) {
          updatePayload.logo_blob_id = this.logoBlobId;
        } else if (this.logoDirty && !this.logo) {
          updatePayload.logo = '';
        }

        await this.$store.dispatch('accounts/update', updatePayload);
        // If user locale is set, update the locale with user locale
        if (this.uiSettings?.locale) {
          this.$root.$i18n.locale = this.uiSettings?.locale;
        } else {
          // If user locale is not set, update the locale with account locale
          this.$root.$i18n.locale = this.locale;
        }
        this.logoBlobId = '';
        this.logoDirty = false;
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.SUCCESS'));
      } catch (error) {
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.ERROR'));
      }
    },
    async handleBrandLogoUpload({ file, url }) {
      if (!checkFileSizeLimit(file, MAXIMUM_LOGO_FILE_SIZE)) {
        useAlert(this.logoUploadSizeErrorMessage);
        return;
      }

      const previousLogo = this.logo;
      this.logo = url || previousLogo;
      this.isUploadingLogo = true;

      try {
        const { fileUrl, blobId } = await uploadFile(file, this.accountId);
        this.logo = normalizeBrandLogoUrl(fileUrl);
        this.logoBlobId = blobId || '';
        this.logoDirty = true;
        useAlert(this.logoUploadSuccessMessage);
      } catch (error) {
        this.logo = previousLogo;
        this.logoBlobId = '';
        useAlert(this.logoUploadErrorMessage);
      } finally {
        this.isUploadingLogo = false;
      }
    },
    handleBrandLogoDelete() {
      this.logo = '';
      this.logoBlobId = '';
      this.logoDirty = true;
    },
    async copyAppId() {
      try {
        await copyTextToClipboard(String(this.id));
        useAlert(this.$t('GENERAL_SETTINGS.FORM.ACCOUNT_ID.COPIED'));
      } catch (error) {
        useAlert(this.$t('GENERAL_SETTINGS.FORM.ACCOUNT_ID.COPY_ERROR'));
      }
    },
  },
};
</script>

<template>
  <div class="settings-page flex flex-col w-full h-full overflow-hidden">
    <!-- Header -->
    <header
      class="settings-header flex items-center justify-between px-6 border-b flex-shrink-0"
      style="height: 50px; background: #ffffff; border-bottom-color: #e5e7eb"
    >
      <div class="flex items-center gap-3">
        <span class="i-ph-gear-six-bold size-5" style="color: #6b7280" />
        <h1 class="text-xl font-semibold" style="color: #111827">
          {{ $t('GENERAL_SETTINGS.TITLE') }}
        </h1>
      </div>
      <button
        class="primary-action-btn"
        :disabled="isUpdating"
        @click="updateAccount"
      >
        <span v-if="isUpdating" class="i-ph-spinner-bold size-4 animate-spin" />
        {{ $t('GENERAL_SETTINGS.SUBMIT') }}
      </button>
    </header>

    <!-- Content -->
    <div class="settings-content flex-1 overflow-y-auto">
      <div class="settings-sections">
        <!-- General Section: Name & Timezone -->
        <SectionLayout
          :title="$t('GENERAL_SETTINGS.FORM.GENERAL_SECTION.TITLE')"
          :description="$t('GENERAL_SETTINGS.FORM.GENERAL_SECTION.NOTE')"
        >
          <form
            v-if="!uiFlags.isFetchingItem"
            class="flex flex-col gap-5"
            @submit.prevent="updateAccount"
          >
            <!-- Name -->
            <div class="settings-input-group">
              <label class="settings-input-label">
                {{ $t('GENERAL_SETTINGS.FORM.NAME.LABEL') }}
              </label>
              <NextInput
                v-model="name"
                type="text"
                class="w-full"
                :placeholder="$t('GENERAL_SETTINGS.FORM.NAME.PLACEHOLDER')"
                @blur="v$.name.$touch"
              />
              <p v-if="v$.name.$error" class="text-xs text-red-500 mt-1">
                {{ $t('GENERAL_SETTINGS.FORM.NAME.ERROR') }}
              </p>
              <p class="text-xs text-n-slate-11 mt-1">
                {{ accountNameHelpText }}
              </p>
            </div>

            <div class="settings-input-group">
              <label class="settings-input-label">{{ brandLogoLabel }}</label>
              <div class="brand-logo-upload">
                <Avatar
                  :src="brandLogoPreview || ''"
                  :name="name || brandLogoLabel"
                  :size="72"
                  allow-upload
                  icon-name="i-lucide-building-2"
                  @upload="handleBrandLogoUpload"
                  @delete="handleBrandLogoDelete"
                />
                <div class="brand-logo-upload__copy">
                  <p class="text-xs text-n-slate-11">
                    {{ brandLogoHelpText }}
                  </p>
                  <p class="text-xs text-n-slate-10 mt-1">
                    {{ brandLogoSizeHelpText }}
                  </p>
                  <p
                    v-if="brandLogoPreview"
                    class="text-xs text-n-slate-10 mt-2"
                  >
                    {{ logoDeleteHelpText }}
                  </p>
                  <p
                    v-if="isUploadingLogo"
                    class="text-xs text-n-slate-10 mt-2"
                  >
                    {{ logoUploadingText }}
                  </p>
                </div>
              </div>
            </div>

            <!-- App ID -->
            <div class="settings-input-group">
              <label class="settings-input-label">{{ appIdLabel }}</label>
              <div class="flex gap-2">
                <NextInput
                  :model-value="String(id)"
                  type="text"
                  class="w-full"
                  disabled
                />
                <NextButton
                  slate
                  faded
                  type="button"
                  icon="i-ph-copy-bold"
                  class="flex-shrink-0"
                  @click="copyAppId"
                />
              </div>
            </div>

            <!-- Timezone -->
            <div class="settings-input-group">
              <label class="settings-input-label">
                {{ $t('GENERAL_SETTINGS.FORM.TIMEZONE.LABEL') }}
              </label>
              <select v-model="timezone" class="settings-select w-full">
                <option
                  v-for="tz in timezoneOptions"
                  :key="tz.value"
                  :value="tz.value"
                >
                  {{ tz.label }}
                </option>
              </select>
              <p v-if="v$.timezone.$error" class="text-xs text-red-500 mt-1">
                {{ $t('GENERAL_SETTINGS.FORM.TIMEZONE.ERROR') }}
              </p>
            </div>
          </form>
        </SectionLayout>

        <!-- Language Section -->
        <SectionLayout
          :title="$t('GENERAL_SETTINGS.FORM.LANGUAGE.LABEL')"
          :description="
            $t('GENERAL_SETTINGS.FORM.LANGUAGE.NOTE') ||
            'Choose the default language for your workspace.'
          "
        >
          <div class="settings-input-group">
            <label class="settings-input-label">
              {{ $t('GENERAL_SETTINGS.FORM.LANGUAGE.LABEL') }}
            </label>
            <select v-model="locale" class="settings-select w-full">
              <option
                v-for="lang in languagesSortedByCode"
                :key="lang.iso_639_1_code"
                :value="lang.iso_639_1_code"
              >
                {{ lang.name }}
              </option>
            </select>
          </div>
        </SectionLayout>

        <!-- Domain & Email Section (if enabled) -->
        <SectionLayout
          v-if="
            featureCustomReplyDomainEnabled || featureCustomReplyEmailEnabled
          "
          :title="$t('GENERAL_SETTINGS.FORM.DOMAIN.LABEL')"
          :description="
            featureInboundEmailEnabled
              ? $t('GENERAL_SETTINGS.FORM.FEATURES.INBOUND_EMAIL_ENABLED')
              : ''
          "
        >
          <div class="flex flex-col gap-5">
            <div
              v-if="featureCustomReplyDomainEnabled"
              class="settings-input-group"
            >
              <label class="settings-input-label">
                {{ $t('GENERAL_SETTINGS.FORM.DOMAIN.LABEL') }}
              </label>
              <NextInput
                v-model="domain"
                type="text"
                class="w-full"
                :placeholder="$t('GENERAL_SETTINGS.FORM.DOMAIN.PLACEHOLDER')"
              />
            </div>
            <div
              v-if="featureCustomReplyEmailEnabled"
              class="settings-input-group"
            >
              <label class="settings-input-label">
                {{ $t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.LABEL') }}
              </label>
              <NextInput
                v-model="supportEmail"
                type="text"
                class="w-full"
                :placeholder="
                  $t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.PLACEHOLDER')
                "
              />
            </div>
          </div>
        </SectionLayout>

        <!-- Chat Buttons Section -->
        <SectionLayout
          :title="$t('GENERAL_SETTINGS.FORM.CHAT_BUTTONS_SECTION.TITLE')"
          :description="$t('GENERAL_SETTINGS.FORM.CHAT_BUTTONS_SECTION.NOTE')"
        >
          <div v-if="!uiFlags.isFetchingItem" class="flex flex-col gap-6">
            <!-- Button 1 -->
            <div class="chat-button-group">
              <h4 class="text-sm font-medium text-n-slate-12 mb-3">
                {{ $t('GENERAL_SETTINGS.FORM.CHAT_BUTTONS_SECTION.BUTTON_1') }}
              </h4>
              <div class="flex flex-col gap-4">
                <div class="settings-input-group">
                  <label class="settings-input-label">
                    {{ $t('GENERAL_SETTINGS.FORM.CHAT_BUTTON_NAME.LABEL') }}
                  </label>
                  <NextInput
                    v-model="chatButton1Name"
                    type="text"
                    class="w-full"
                    :placeholder="
                      $t('GENERAL_SETTINGS.FORM.CHAT_BUTTON_NAME.PLACEHOLDER')
                    "
                  />
                </div>
                <div class="settings-input-group">
                  <label class="settings-input-label">
                    {{ $t('GENERAL_SETTINGS.FORM.CHAT_BUTTON_URL.LABEL') }}
                  </label>
                  <NextInput
                    v-model="chatButton1Url"
                    type="url"
                    class="w-full"
                    :placeholder="
                      $t('GENERAL_SETTINGS.FORM.CHAT_BUTTON_URL.PLACEHOLDER')
                    "
                    @blur="v$.chatButton1Url.$touch"
                  />
                  <p v-if="v$.chatButton1Url.$error" class="text-xs text-red-500 mt-1">
                    {{ $t('GENERAL_SETTINGS.FORM.CHAT_BUTTON_URL_INVALID') }}
                  </p>
                  <p class="text-xs text-n-slate-11 mt-1">
                    {{ $t('GENERAL_SETTINGS.FORM.CHAT_BUTTON_URL.HELP') }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Button 2 -->
            <div class="chat-button-group">
              <h4 class="text-sm font-medium text-n-slate-12 mb-3">
                {{ $t('GENERAL_SETTINGS.FORM.CHAT_BUTTONS_SECTION.BUTTON_2') }}
              </h4>
              <div class="flex flex-col gap-4">
                <div class="settings-input-group">
                  <label class="settings-input-label">
                    {{ $t('GENERAL_SETTINGS.FORM.CHAT_BUTTON_NAME.LABEL') }}
                  </label>
                  <NextInput
                    v-model="chatButton2Name"
                    type="text"
                    class="w-full"
                    :placeholder="
                      $t('GENERAL_SETTINGS.FORM.CHAT_BUTTON_NAME.PLACEHOLDER')
                    "
                  />
                </div>
                <div class="settings-input-group">
                  <label class="settings-input-label">
                    {{ $t('GENERAL_SETTINGS.FORM.CHAT_BUTTON_URL.LABEL') }}
                  </label>
                  <NextInput
                    v-model="chatButton2Url"
                    type="url"
                    class="w-full"
                    :placeholder="
                      $t('GENERAL_SETTINGS.FORM.CHAT_BUTTON_URL.PLACEHOLDER')
                    "
                    @blur="v$.chatButton2Url.$touch"
                  />
                  <p v-if="v$.chatButton2Url.$error" class="text-xs text-red-500 mt-1">
                    {{ $t('GENERAL_SETTINGS.FORM.CHAT_BUTTON_URL_INVALID') }}
                  </p>
                  <p class="text-xs text-n-slate-11 mt-1">
                    {{ $t('GENERAL_SETTINGS.FORM.CHAT_BUTTON_URL.HELP') }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Button 3 -->
            <div class="chat-button-group">
              <h4 class="text-sm font-medium text-n-slate-12 mb-3">
                {{ $t('GENERAL_SETTINGS.FORM.CHAT_BUTTONS_SECTION.BUTTON_3') }}
              </h4>
              <div class="flex flex-col gap-4">
                <div class="settings-input-group">
                  <label class="settings-input-label">
                    {{ $t('GENERAL_SETTINGS.FORM.CHAT_BUTTON_NAME.LABEL') }}
                  </label>
                  <NextInput
                    v-model="chatButton3Name"
                    type="text"
                    class="w-full"
                    :placeholder="
                      $t('GENERAL_SETTINGS.FORM.CHAT_BUTTON_NAME.PLACEHOLDER')
                    "
                  />
                </div>
                <div class="settings-input-group">
                  <label class="settings-input-label">
                    {{ $t('GENERAL_SETTINGS.FORM.CHAT_BUTTON_URL.LABEL') }}
                  </label>
                  <NextInput
                    v-model="chatButton3Url"
                    type="url"
                    class="w-full"
                    :placeholder="
                      $t('GENERAL_SETTINGS.FORM.CHAT_BUTTON_URL.PLACEHOLDER')
                    "
                    @blur="v$.chatButton3Url.$touch"
                  />
                  <p v-if="v$.chatButton3Url.$error" class="text-xs text-red-500 mt-1">
                    {{ $t('GENERAL_SETTINGS.FORM.CHAT_BUTTON_URL_INVALID') }}
                  </p>
                  <p class="text-xs text-n-slate-11 mt-1">
                    {{ $t('GENERAL_SETTINGS.FORM.CHAT_BUTTON_URL.HELP') }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </SectionLayout>

        <!-- Auto Resolve Section -->
        <AutoResolve v-if="showAutoResolutionConfig" />

        <!-- Audio Transcription Section -->
        <AudioTranscription v-if="showAudioTranscriptionConfig" />

        <!-- IC Number Validation Section -->
        <IcNumberValidation />

        <!-- Account Delete Section (only on Chatwoot Cloud) -->
        <AccountDelete v-if="!uiFlags.isFetchingItem && isOnChatwootCloud" />

        <!-- Build Info -->
        <BuildInfo />

        <!-- Loading State -->
        <woot-loading-state v-if="uiFlags.isFetchingItem" />
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Force white theme for both light and dark modes */
.settings-page,
.dark .settings-page {
  background-color: #ffffff !important;
  color: #111827 !important;
}

.settings-header {
  background-color: #ffffff !important;
  border-bottom-color: #e5e7eb !important;
}

.settings-content {
  background-color: #ffffff !important;
}

/* Labels */
.settings-input-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
}

/* Selects — match NextInput outline style */
.settings-select {
  appearance: none;
  background-color: #ffffff;
  border: none;
  outline: 1px solid #d1d5db;
  border-radius: 0.5rem;
  padding: 0.625rem 0.75rem;
  font-size: 0.875rem;
  color: #111827;
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem;
}

.settings-select:hover {
  outline-color: #9ca3af;
}

.settings-select:focus {
  outline: 2px solid #0066ff;
  outline-offset: -1px;
}

/* Chat button groups */
.chat-button-group {
  padding: 1rem;
  background-color: #f9fafb;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
}

.brand-logo-upload {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.brand-logo-upload__copy {
  min-width: 0;
  max-width: 28rem;
}

/* Override NextInput & NextButton inside this page */
:deep(.text-n-slate-12) {
  color: #111827 !important;
}
:deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
:deep(.text-n-slate-10) {
  color: #6b7280 !important;
}
:deep(.bg-n-alpha-black2) {
  background-color: #ffffff !important;
}
:deep(.bg-n-background) {
  background-color: #ffffff !important;
}
:deep(.bg-n-solid-1) {
  background-color: #ffffff !important;
}
:deep(.bg-n-solid-2) {
  background-color: #f9fafb !important;
}
:deep(.border-n-weak) {
  border-color: #e5e7eb !important;
}
:deep(.outline-n-weak) {
  outline-color: #9ca3af !important;
}
:deep(.outline-n-slate-6) {
  outline-color: #6b7280 !important;
}
:deep(label) {
  color: #374151 !important;
}
:deep(input:not([disabled])) {
  background-color: #ffffff !important;
  color: #111827 !important;
}
:deep(input::placeholder) {
  color: #94a3b8 !important;
}
:deep(input[disabled]) {
  background-color: #f3f4f6 !important;
  color: #374151 !important;
  opacity: 1 !important;
}

.primary-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  font-family:
    'Excon',
    system-ui,
    -apple-system,
    sans-serif;
  background-color: #111827;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.primary-action-btn:hover {
  background-color: #1f2937;
}

.primary-action-btn:active {
  transform: scale(0.98);
}

.primary-action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
